import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Radio, Video, Calendar, Megaphone, MessageSquare, Building2, Users, UserPlus, LogOut, Cross, ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

const NAV = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Tableau de bord", testid: "overview" },
  { to: "/admin/live", icon: Radio, label: "Direct", testid: "live" },
  { to: "/admin/cultes", icon: Video, label: "Cultes", testid: "cultes" },
  { to: "/admin/events", icon: Calendar, label: "Événements", testid: "events" },
  { to: "/admin/announcements", icon: Megaphone, label: "Annonces", testid: "announcements" },
  { to: "/admin/messages", icon: MessageSquare, label: "Messages", testid: "messages" },
  { to: "/admin/registrations", icon: UserPlus, label: "Inscriptions", testid: "registrations" },
  { to: "/admin/church", icon: Building2, label: "Église", testid: "church" },
  { to: "/admin/users", icon: Users, label: "Administrateurs", testid: "users", superOnly: true },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);

  useEffect(() => {
    api.get("/church-info").then((r) => setInfo(r.data)).catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
    toast.success("Déconnexion réussie");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="admin-layout">
      <aside className="hidden lg:flex flex-col w-72 bg-deepblue-900 text-white sticky top-0 h-screen">
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            {info?.logo_url ? (
              <img src={info.logo_url} alt={info?.name || "Logo"} className="w-12 h-12 rounded-full object-cover ring-2 ring-gold/60 bg-white" />
            ) : (
              <span className="w-10 h-10 rounded-full bg-gold/20 text-gold flex items-center justify-center">
                <Cross className="w-5 h-5" strokeWidth={2.4} />
              </span>
            )}
            <div>
              <div className="font-serif text-lg leading-tight">Espace admin</div>
              <div className="text-xs text-gold uppercase tracking-widest truncate max-w-[160px]">{info?.name || "Église"}</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {NAV.filter((n) => !n.superOnly || user?.role === "super_admin").map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`sidebar-${n.testid}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  isActive ? "bg-gold text-white shadow-lg" : "text-white/70 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10 space-y-3">
          <a href="/" target="_blank" rel="noopener noreferrer" data-testid="view-site-link"
            className="flex items-center gap-2 text-white/70 hover:text-gold text-sm px-4 py-2">
            <ExternalLink className="w-4 h-4" /> Voir le site
          </a>
          <div className="px-4 py-3 rounded-xl bg-white/5">
            <div className="text-xs text-white/50">Connecté en tant que</div>
            <div className="text-sm text-white truncate">{user?.email}</div>
          </div>
          <Button onClick={handleLogout} data-testid="logout-btn"
            variant="outline" className="w-full border-white/20 bg-transparent text-white hover:bg-white hover:text-deepblue-900">
            <LogOut className="w-4 h-4 mr-2" /> Se déconnecter
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        {/* Mobile bar */}
        <div className="lg:hidden sticky top-0 z-30 bg-deepblue-900 text-white px-4 py-3 flex items-center justify-between">
          <div className="font-serif">Admin</div>
          <div className="flex items-center gap-2 overflow-x-auto text-xs">
            {NAV.filter((n) => !n.superOnly || user?.role === "super_admin").map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end}
                className={({ isActive }) => `px-3 py-1.5 rounded-full whitespace-nowrap ${isActive ? "bg-gold" : "bg-white/10"}`}>
                {n.label}
              </NavLink>
            ))}
            <button onClick={handleLogout} className="px-3 py-1.5 rounded-full bg-red-500">Quitter</button>
          </div>
        </div>

        <div className="p-6 lg:p-10 max-w-7xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
